import _ from 'lodash';

import { parseNumber } from '@/src/util/numbers';

import { TaxCalculatorMap } from './components/TreatmentForm/useTreatmentCalculator';
import { SUPPLIER_TYPES } from './Config';
import { TYPE_TITLE } from './const/treatment';
import { calculateTaxRate, TaxCalculationMap } from './models/Treatment/TreatmentCalculator';
import {
  CalculationStrategy,
  Currency,
  DropdownOption,
  LineEntry,
  PublishData,
  RemoteOptions,
  RemoteOptionsResponse,
} from './types';
import {
  AutomatedDraft,
  AutomatedDraftResponse,
  CheckPaymentDraft,
  Draft,
  DraftResponse,
  PaidBillDraft,
  UnpaidBillDraft,
} from './types/draft';
import { LineEntryForm, Treatment, TreatmentForm, TreatmentResponse } from './types/treatment';

type DROPDOWN_OPTIONS = {
  valueField?: string;
  publishDataIsKey?: boolean;
  otherProps?: object;
  otherFields?: object;
};

const DEFAULT_DROPDOWN_OPTIONS: DROPDOWN_OPTIONS = {
  valueField: null,
  publishDataIsKey: false,
  otherProps: {},
  otherFields: {},
};

export const mapDropdownOption = (
  object: {
    [key: string | number]: any;
  },
  {
    valueField = null,
    otherProps = {},
    publishDataIsKey = false,
  }: DROPDOWN_OPTIONS = DEFAULT_DROPDOWN_OPTIONS
) => {
  const key = object[0];
  const value = object[1];
  const label = valueField ? value[valueField] : value;
  const publishData = publishDataIsKey
    ? key
    : {
        id: key,
        name: label,
      };

  return {
    value: key,
    label,
    ...otherProps,
    publishData,
  };
};

export const mapDropdownOptions = (
  rawOptions: {
    [key: string | number]: any;
  },
  settings?: DROPDOWN_OPTIONS
) => {
  const options = [];

  for (const object of Object.entries(rawOptions)) {
    options.push(mapDropdownOption(object, settings));
  }

  return options;
};

export const mapObjectDropdownOptions = (
  rawObjects: {
    [key: string | number]: any;
  },
  field: string,
  otherFields: string[] = []
) => {
  const accounts = [];

  for (const object of Object.entries(rawObjects)) {
    const otherProps = otherFields.length > 0 ? _.pick(object[1], otherFields) : {};

    accounts.push(
      mapDropdownOption(object, {
        valueField: field,
        otherProps,
      })
    );
  }

  return accounts;
};

export const mapCurrencyDropdownOptions = <T extends { id: string | number; name: string }>(
  srcOptions: T[]
): DropdownOption[] => {
  return srcOptions.map((srcOption) => {
    return {
      label: srcOption.name,
      value: srcOption.id,
      publishData: {
        id: srcOption.id + '',
        name: srcOption.name,
      },
    };
  });
};

export const mapTaxCodesDropdownOptions = (rawTaxCodes: RemoteOptionsResponse['taxCodes']) => {
  const accounts = [];

  for (const taxCode of Object.entries(rawTaxCodes)) {
    const key = taxCode[0];
    const valueObj = taxCode[1];
    const taxRate = calculateTaxRate(valueObj.rates);

    accounts.push({
      ...mapDropdownOption(taxCode, { valueField: 'name' }),
      publishData: {
        id: key,
        name: valueObj.name,
        rates: valueObj.rates,
      },
      label: `${valueObj.name} (${taxRate}%)`,
      taxRate,
    });
  }

  return accounts;
};

export const mapItemsDropdownOptions = (rawItems: RemoteOptionsResponse['items']) => {
  const items = [];

  for (const item of Object.entries(rawItems)) {
    items.push({
      ...mapDropdownOption(item, { valueField: 'name' }),
    });
  }

  return items;
};

export const mapLineItemDropdownOptions = ({
  rawItems,
  rawAccounts,
  rawTaxCodes,
  rawClasses,
  rawTaxCalculation,
}: {
  rawItems: RemoteOptionsResponse['items'];
  rawAccounts: RemoteOptionsResponse['accounts'];
  rawTaxCodes: RemoteOptionsResponse['taxCodes'];
  rawClasses: RemoteOptionsResponse['classes'];
  rawTaxCalculation: RemoteOptionsResponse['taxCalculation'];
}) => {
  // const items = mapItemsDropdownOptions(rawItems);
  const items = mapObjectDropdownOptions(rawItems, 'name');
  const accounts = mapObjectDropdownOptions(rawAccounts, 'name', ['type']);
  const taxCodes = mapTaxCodesDropdownOptions(rawTaxCodes);
  const classes = mapDropdownOptions(rawClasses);
  const taxCalculation = mapTaxCalculationDropdownOptions(rawTaxCalculation);

  return {
    items,
    accounts,
    taxCodes,
    classes,
    taxCalculation,
  };
};

// Suppliers + employees
export const mapPayeeOptions = (
  rawSuppliers: RemoteOptionsResponse['vendors'],
  rawEmployees: RemoteOptionsResponse['employees'] = {}
) => {
  const payees = [];
  for (const supplier of Object.entries(rawSuppliers)) {
    payees.push(
      mapDropdownOption(supplier, {
        otherProps: {
          type: SUPPLIER_TYPES.SUPPLIER,
          currency: supplier[1].currency,
        },
        valueField: 'name',
      })
    );
  }

  for (const employee of Object.entries(rawEmployees)) {
    payees.push(
      mapDropdownOption(employee, {
        otherProps: {
          type: SUPPLIER_TYPES.PAYEE,
        },
      })
    );
  }
  return _.sortBy(payees, ['label']);
};

const mapTaxCalculationDropdownOptions = (
  taxCalculation: RemoteOptionsResponse['taxCalculation']
) => {
  return mapDropdownOptions(taxCalculation, { publishDataIsKey: true }).map((tc) => ({
    ...tc,
    strategy: TaxCalculatorMap[tc.value],
  }));
};

export const mapFormData = (
  formData: Omit<TreatmentForm, 'lineItems' | 'lineAccounts' | 'currency'>
): Treatment['payload'] => {
  const formattedFormData = {} as Treatment['payload'];

  for (const [key, value] of Object.entries(formData)) {
    type PublishDataWithType = PublishData & { type: string };

    for (const [key, value] of Object.entries(formData)) {
      if (key === 'payee' && value && typeof value === 'object') {
        const publishData = value.publishData as PublishDataWithType;
        formattedFormData[key] = {
          id: typeof publishData === 'object' ? publishData.id : key || '',
          name: typeof publishData === 'object' ? publishData.name : '',
          type: (value as PublishDataWithType)?.type === 'Supplier' ? 'Vendor' : 'Employee',
        } as PublishDataWithType;
      } else if (value && typeof value === 'object') {
        formattedFormData[key] = value.publishData;
      } else {
        formattedFormData[key] = value;
      }
    }
  }

  return formattedFormData;
};

const mapLineDefaultData = (lineItem: LineEntryForm) => {
  return {
    ...lineItem,
    taxCode: lineItem.taxCode ? lineItem.taxCode.publishData : null,
    totalAmount: lineItem.totalAmount,
    class: lineItem.class ? lineItem.class.publishData : null,
    description: lineItem.description,
    qty: lineItem.qty,
    unitPrice: lineItem.unitPrice,
  };
};

const mapLineItemPublishData = (lineItem: LineEntryForm) => {
  return {
    ...mapLineDefaultData(lineItem),
    item: {
      id: lineItem.item?.publishData.id,
      name: lineItem.item?.publishData.name,
      description: lineItem.description,
    },
  };
};

const mapLineAccountPublishData = (lineItem: LineEntryForm) => {
  return {
    ...mapLineDefaultData(lineItem),
    account: lineItem.account?.publishData,
  };
};

export const mapLineItemsAndAccountsPublishData = (
  lineItems: LineEntryForm[],
  treatmentCalculationStrategy: CalculationStrategy,
  isPublish: boolean
) => {
  const formattedLineItems = [];
  const formattedLineAccounts = [];
  const recalculatedLineItems: LineEntryForm[] = isPublish
    ? treatmentCalculationStrategy.recalculateLineItemsAmount(lineItems)
    : lineItems;

  for (const lineItem of Object.values(recalculatedLineItems)) {
    if (lineItem.item) {
      formattedLineItems.push(mapLineItemPublishData(lineItem));
    } else {
      formattedLineAccounts.push(mapLineAccountPublishData(lineItem));
    }
  }

  return {
    lineItems: formattedLineItems,
    lineAccounts: formattedLineAccounts,
  };
};

export const findCurrencyOptionFromCurrencies = (
  currencies: RemoteOptions['currencies'],
  currencyId: string
) => {
  const currency = currencies.find((currency) => currency.value === currencyId);

  if (currency) {
    return currency;
  } else {
    return null;
  }
};

export const findCurrencyFromCurrencies = (
  currencies: RemoteOptions['currencies'],
  currencyId: string
): Currency => {
  const currency = currencies.find((currency) => currency.value === currencyId);

  if (currency) {
    return {
      ...currency.publishData,
      default: false,
    };
  } else {
    return null;
  }
};

export const findValueFromRemoteOption = (
  value: PublishData,
  remoteOption: DropdownOption[]
): any => {
  return (
    remoteOption.find((remoteOptionItem) => _.isEqual(remoteOptionItem.publishData, value)) ?? null
  );
};

export const mapDefaultRemoteOptions = (responseData: RemoteOptionsResponse) => {
  const lineItemDropdownOptions = mapLineItemDropdownOptions({
    rawItems: responseData.items,
    rawAccounts: responseData.accounts,
    rawTaxCodes: responseData.taxCodes,
    rawClasses: responseData.classes,
    rawTaxCalculation: responseData.taxCalculation,
  });

  return {
    lineItems: lineItemDropdownOptions,
    defaultMemo: responseData.defaultMemo,
    defaultCurrency: responseData.defaultCurrency,
    currencies: mapCurrencyDropdownOptions(responseData.currencies),
    terms: mapDropdownOptions(responseData.terms),
    locations: mapDropdownOptions(responseData.locations),
    paymentAccounts: mapObjectDropdownOptions(responseData.paymentAccounts, 'name'),
    bankAccounts: mapObjectDropdownOptions(responseData.bankAccounts, 'name'),
    paymentMethods: mapDropdownOptions(responseData.paymentMethods),
  };
};

export const mapDraftValueToTreatmentForm = (
  draft: Draft | AutomatedDraft,
  remoteOptions: RemoteOptions
): TreatmentForm => {
  const payload = draft.payload;
  const payeeVendor =
    draft.type === 'Bill'
      ? (payload as UnpaidBillDraft['payload']).vendor
      : (payload as PaidBillDraft['payload']).payee;

  const treatment: TreatmentForm = {
    ...payload,
    bankAccount: findValueFromRemoteOption(
      (payload as CheckPaymentDraft['payload']).bankAccount,
      remoteOptions.bankAccounts
    ),
    paymentAccount: findValueFromRemoteOption(
      (payload as PaidBillDraft['payload']).paymentAccount,
      remoteOptions.paymentAccounts
    ),
    location: findValueFromRemoteOption(payload.location, remoteOptions.locations),
    payee: remoteOptions.payees.find((payee) => payee.label === payeeVendor?.name),
    paymentMethod: findValueFromRemoteOption(
      (payload as PaidBillDraft['payload']).paymentMethod,
      remoteOptions.paymentMethods
    ),
    salesTerm: findValueFromRemoteOption(
      (payload as UnpaidBillDraft['payload']).salesTerm,
      remoteOptions.terms
    ),
    currency:
      findCurrencyFromCurrencies(remoteOptions.currencies, payload.currency?.id) ||
      remoteOptions.defaultCurrency,
    taxCalculation: remoteOptions.lineItems.taxCalculation.find(
      (taxCalculation) => taxCalculation.value === payload.taxCalculation
    ),
    dueDate: payload.dueDate || null,
    memo: payload?.memo ?? remoteOptions.defaultMemo,
    lineItems: [], // will be managed by state
    lineAccounts: [], // will be managed by state
  };

  return treatment;
};

const getParsedDraftLineEntryComputableValues = (
  lineItem: LineEntry
): { qty: string; unitPrice: string; totalAmount: string } => {
  const totalAmount = Number(lineItem.totalAmount).toFixed(2);
  return {
    qty: lineItem.qty ? Number(lineItem.qty).toFixed(2) : '1.00',
    unitPrice: lineItem.unitPrice ? Number(lineItem.unitPrice).toFixed(2) : totalAmount ?? '0.00',
    totalAmount: totalAmount ?? '0.00',
  };
};

export const mapDraftLineItems = (
  lineItems: Draft['payload']['lineItems'],
  lineItemsRemoteOptions: RemoteOptions['lineItems']
): LineEntryForm[] => {
  return lineItems.map((lineItem) => {
    const { qty, unitPrice, totalAmount } = getParsedDraftLineEntryComputableValues(lineItem);
    return {
      ...lineItem,
      qty,
      unitPrice,
      totalAmount,
      item: findValueFromRemoteOption(lineItem.item, lineItemsRemoteOptions.items),
      account: findValueFromRemoteOption(lineItem.account, lineItemsRemoteOptions.accounts),
      class: findValueFromRemoteOption(lineItem.class, lineItemsRemoteOptions.classes),
      taxCode: lineItem.taxCode
        ? lineItemsRemoteOptions.taxCodes.find(
            (remoteOption) => lineItem.taxCode.id === remoteOption.value
          )
        : null,
    };
  });
};

export const mapLineItems = (
  lineItems: LineEntryForm[] | LineEntry[],
  lineItemsRemoteOptions: RemoteOptions['lineItems']
): LineEntryForm[] => {
  return lineItems.map((lineItem) => {
    const { qty, unitPrice, totalAmount } = getParsedDraftLineEntryComputableValues(lineItem);
    return {
      ...lineItem,
      qty,
      unitPrice,
      totalAmount,
      item: findValueFromRemoteOption(lineItem.item, lineItemsRemoteOptions.items),
      account: findValueFromRemoteOption(lineItem.account, lineItemsRemoteOptions.accounts),
      class: findValueFromRemoteOption(lineItem.class, lineItemsRemoteOptions.classes),
      taxCode: lineItem.taxCode
        ? lineItemsRemoteOptions.taxCodes.find(
            (remoteOption) => lineItem.taxCode.id === remoteOption.value
          )
        : null,
    };
  });
};

const mapLineAccountTaxRates = (lineItems: LineEntry[]) => {
  return lineItems.map((lineItem) => ({
    ...lineItem,
    taxCode: {
      ...lineItem.taxCode,
      taxRate:
        lineItem.taxCode && lineItem.taxCode.rates ? calculateTaxRate(lineItem.taxCode.rates) : 0,
    },
  }));
};

const mapTreatmentData = (treatment: TreatmentResponse): Treatment => {
  const payload = JSON.parse(treatment.payload);
  let taxCalculation =
    typeof payload.taxCalculation === 'object'
      ? payload.taxCalculation.name
      : TaxCalculationMap[payload.taxCalculation];

  if (!taxCalculation) {
    taxCalculation = payload.taxRqmt ? payload.taxRqmt.name : null;
  }

  return {
    ...treatment,
    typeDisplayName: TYPE_TITLE[treatment.type],
    payload: {
      ...payload,
      lineItems: [
        ...mapLineAccountTaxRates(payload.lineAccounts),
        ...mapLineAccountTaxRates(payload.lineItems),
      ],
      taxCalculation,
      // taxCalculation: typeof payload.taxCalculation === 'object'
      //   ? payload.taxCalculation.name
      //   : TaxCalculationMap[payload.taxCalculation],
    },
  };
};

const getComputedTotalOfAutomatedDraft = ({ transaction, remoteOptions }) => {
  const taxCodeOptions = mapTaxCodesDropdownOptions(remoteOptions.taxCodes);

  const parsedLineItems = transaction.lineItems.map((lineItem) => {
    const taxCode = taxCodeOptions.find((option) => option.value === lineItem.taxCode.id);
    const computableValues = getParsedDraftLineEntryComputableValues(lineItem);
    return {
      ...lineItem,
      ...computableValues,
      taxCode,
    };
  });

  const parsedLineAccounts = transaction.lineAccounts.map((lineItem) => {
    const taxCode = taxCodeOptions.find((option) => option.value === lineItem.taxCode.id);
    const computableValues = getParsedDraftLineEntryComputableValues(lineItem);
    return {
      ...lineItem,
      ...computableValues,
      taxCode,
    };
  });

  const taxCalculation = TaxCalculatorMap[transaction.taxCalculation];
  const { totals } = taxCalculation.calculateLineItemsAndTotals({
    lineItems: [...parsedLineAccounts, ...parsedLineItems],
  });

  return totals;
};

export const mapDraftData = (
  draft: DraftResponse,
  remoteOptions?: RemoteOptionsResponse
): Draft | AutomatedDraft => {
  const { transaction, ...rest } = draft;

  const mappedDraft = {
    ...rest,
    typeDisplayName: TYPE_TITLE[draft.type],
    transactionDate: draft.transaction.transactionDate || null,
    payload: transaction as Draft['payload'],
  };

  if (draft.automationId && !draft.modifiedBy && transaction.taxCalculation && remoteOptions) {
    // @NOTE: CFO-1271
    const totals = getComputedTotalOfAutomatedDraft({ transaction, remoteOptions });

    return {
      ...mappedDraft,
      payload: {
        ...mappedDraft.payload,
        totalAmount: parseNumber(totals.total),
      },
    };
  }

  return mappedDraft;
};

export const mapTreatments = (
  treatment: TreatmentResponse | TreatmentResponse[]
): Treatment[] | Treatment => {
  return _.isArray(treatment)
    ? treatment.map((TreatmentResponse) => mapTreatmentData(TreatmentResponse))
    : mapTreatmentData(treatment);
};

export const mapDrafts = (
  drafts: DraftResponse[] | AutomatedDraftResponse[],
  remoteOptions: RemoteOptionsResponse
): (Draft | AutomatedDraft)[] => drafts.map((draftItem) => mapDraftData(draftItem, remoteOptions));
